import { makeStyles, createStyles, Theme, Box, Chip } from "@material-ui/core";
import clsx from "clsx";

export type AdminCellProps = {
  title: string;
  subtitle: string;
  status?: string | undefined;
  selected?: boolean;
  type?: "error" | "warning" | "success" | "default";
  loading?: boolean;
};

export const AdminCell = ({
  title,
  subtitle,
  selected,
  status,
  type = "default",
}: AdminCellProps) => {
  const classes = useStyles();

  return (
    <div className={classes.cell}>
      <div className={selected ? clsx(classes.cellShadow, classes.activeCell) : classes.cellShadow}>
        <Box display="flex" justifyContent="space-between">
          <div className={classes.cellTitle}>{title}</div>
          {typeof status !== "undefined" && (
            <Chip
              label={status}
              size="small"
              variant="outlined"
              className={clsx(classes.chip, classes[type])}
            />
          )}
        </Box>
        <div className={classes.cellSubtitle}>{subtitle}</div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cell: {
      fontFamily: theme.typography.fontFamily,
      flexGrow: 1,
      boxShadow: `inset 0 1px 0 0 ${theme.palette.background.default}, inset 0 -1px 0 0 ${theme.palette.background.default}`,
      backgroundColor: "white",
      display: "flex",
      flexDirection: "column",
      transition: "all ease-in-out .1s",
      "&:hover": {
        boxShadow: "inset 0 1px 0 0 transparent, inset 0 -1px 0 0 transparent",
      },
    },
    cellShadow: {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(4),
      "&:hover": {
        backgroundColor: theme.palette.background.default,
        cursor: "pointer",
      },
    },
    activeCell: {
      backgroundColor: "rgba(26, 99, 171, 0.05)",
      boxShadow: `inset 4px 0 0 0 ${theme.palette.primary.main}`,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.background.default,
      },
    },
    cellTitle: {
      fontSize: 16,
      lineHeight: "21px",
      fontWeight: 400,
      color: theme.palette.dark.main,
      display: "flex",
      justifyContent: "space-between",
    },
    cellSubtitle: {
      fontSize: 12,
      fontWeight: "bold",
      lineHeight: 1.6,
      paddingTop: "3px",
      paddingBottom: "5px",
      color: theme.palette.blueGrey.main,
      letterSpacing: 1,
    },
    chip: {
      textTransform: "uppercase",
      borderRadius: 2,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.83,
      lineHeight: 1.9,
      height: "16px",
      border: `1px solid ${theme.palette.blueGrey.main}`,

      "& span": {
        padding: "0 4px",
      },
    },
    default: {
      borderColor: theme.palette.blueGrey.main,
      color: theme.palette.blueGrey.main,
    },
    error: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
    warning: {
      borderColor: theme.palette.warning.main,
      color: theme.palette.warning.main,
    },
    success: {
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main,
    },
  }),
);
